import React, { useContext } from "react";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import styles from "./AiCreditorHint.module.css";

/**
 * Creditor-side counterpart of AiRealestateHint. Renders Peter Schiratzki's
 * Variant A styling (info-circle icon in dark amber on a white surface) for
 * the two AI-outcome cases that need user attention on the creditor field:
 * ambiguous-but-unresolved, and no-match / no-query.
 *
 * The creditor section is full-width with no companion column, so this
 * component doesn't need the mirror-invisible-spacer trick that
 * AiRealestateHint uses to keep the Objekt / Auftrag columns aligned.
 */
const AiCreditorHintBase = () => {
    const { t } = useTranslation();
    const { invoiceStore, invoiceFilePreviewStore } = useContext(RootStoreContext);

    const aiDetection = invoiceStore.currentInvoice?.aiDetection;
    const outcome = aiDetection?.results.creditorid?.outcome;

    const noCreditorPicked = !invoiceStore.currentInvoice?.creditor?.id;
    const notUniqueVisible = !!aiDetection && outcome === "ambiguous" && noCreditorPicked;
    const notFoundVisible =
        !invoiceFilePreviewStore.isAiPending &&
        (outcome === "no_query" || outcome === "no_match") &&
        noCreditorPicked;

    let message: string | undefined;
    if (notUniqueVisible) {
        message = t("screens.kredi_flow.ai.creditor_not_unique");
    } else if (notFoundVisible) {
        message = t("screens.kredi_flow.ai.creditor_not_found");
    }

    if (!message) {
        return null;
    }

    return (
        <div className={styles.AiCreditorHint}>
            <UI.Icon icon={UI.SVGIcon.InformationFilled} size={16} color="#C26A00" />
            <span>{message}</span>
        </div>
    );
};

export const AiCreditorHint = observer(AiCreditorHintBase);
